import React from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, InputBase, List, styled } from '@mui/material';
import { ListItemLink } from 'components/mui/ListItemLink';
import useDebouncedSearch from 'hooks/useDebouncedSearch';
import { CompanyType } from 'models/CompanyModel';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';


const Search = styled('form')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
}));

const useSearch = () => useDebouncedSearch(input => Api.util.search(input), { wait: 1000, minLength: 3 });

type Result = {
    label: string,
    id: number,
    type: string,
    status: string,
}

const partyTypes = new Set(['consignee', 'dealer', 'notify']);
const verbatimTypes = new Set(['car', 'voyage', 'invoice']);

const path = (r: Result) => {
    if (verbatimTypes.has(r.type))
        return `/${r.type}/${r.id}`;

    if (r.type === 'shipper')
        return `/companies/shippers?id=${r.id}`;

    if (partyTypes.has(r.type))
        return `/parties?type=${r.type}&id=${r.id}`;

    return '/';
}


const SearchField = () => {
    const { t } = useTranslation();
    const my = useRecoilValue(auth);
    const navigate = useNavigate();
    const { input, setInput, promise } = useSearch();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (promise.result?.data.length === 1) {
            navigate(path(promise.result.data[0]));
            setInput('');
        }
    }

    return (
        <Search onSubmit={submit}>
            <SearchIconWrapper>
                <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
                placeholder={t('search')}
                inputProps={{
                    'aria-label': 'search',
                    inputMode: my.type === CompanyType.PortAgent ? 'numeric' : undefined,
                }}
                value={input}
                onChange={e => setInput(e.target.value)}
                onBlur={() => setTimeout(() => setInput(''), 200)}
            />
            {!!promise.result?.data.length && (
                <List dense sx={{position: 'absolute', bgcolor: 'background.paper', color: 'black', boxShadow: 3, width: '100%'}}>
                    {promise.result.data.map((r: Result) =>
                        <ListItemLink
                            key={r.type + r.id}
                            to={path(r)}
                            primary={r.label}
                            secondary={`${r.status || ''} ${r.type}`}
                        />
                    )}
                </List>
            )}
        </Search>
    )
};

export default SearchField;
